import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { AdminList, AdminListRow } from "@/modules/shell/AdminList";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  getDocSeries,
  updateDocSeries,
  type DocSeries,
} from "@/shared/api/series";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

const DOC_TYPES = [
  { id: "sales_order", label: "Sales order" },
  { id: "purchase_order", label: "Purchase order" },
  { id: "goods_receipt", label: "Goods receipt" },
  { id: "sales_return", label: "Sales return" },
  { id: "journal_entry", label: "Journal entry" },
] as const;

type Draft = {
  mode: DocSeries["mode"];
  prefix: string;
  padWidth: string;
};

/** Company doc number series — plan/23 */
export function DocSeriesSettingsPage() {
  const { companySlug } = useParams();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const seriesQuery = useQuery({
    queryKey: queryKeys.docSeries.all(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: async () => {
      const results = await Promise.all(
        DOC_TYPES.map((d) => getDocSeries(companySlug!, d.id)),
      );
      return results.map((r) => r.series);
    },
  });
  const errorMsg = queryErrorMessage(seriesQuery.error, seriesQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  const rows = seriesQuery.data ?? [];

  useEffect(() => {
    if (!seriesQuery.data) return;
    const next: Record<string, Draft> = {};
    for (const s of seriesQuery.data) {
      next[s.docType] = {
        mode: s.mode,
        prefix: s.prefix,
        padWidth: String(s.padWidth),
      };
    }
    setDrafts(next);
  }, [seriesQuery.data]);

  if (!companySlug) return null;

  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  async function onSave(docType: string) {
    const draft = drafts[docType];
    if (!draft) return;
    const pad = Number(draft.padWidth);
    if (!Number.isInteger(pad) || pad < 1 || pad > 12) {
      setError("Pad width must be 1–12");
      return;
    }
    if (!draft.prefix.trim()) {
      setError("Prefix required");
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { series } = await updateDocSeries(companySlug!, docType, {
        mode: draft.mode,
        prefix: draft.prefix.trim(),
        padWidth: pad,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.docSeries.all(companySlug!),
      });
      setDrafts((prev) => ({
        ...prev,
        [docType]: {
          mode: series.mode,
          prefix: series.prefix,
          padWidth: String(series.padWidth),
        },
      }));
      const label =
        DOC_TYPES.find((d) => d.id === docType)?.label ?? docType;
      setStatus(`Saved ${label} · next ${series.suggested}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
      <PageFrame>
      <PageHeader
        title="Document numbers"
        description="Mode, prefix, and padding per doc type. Auto is the happy path; manual modes for shops that print their own numbers."
      />

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}
      {status && (
        <Alert variant="muted" className="mt-4">
          {status}
        </Alert>
      )}

      <div className="mt-8">
        <AdminList>
          {DOC_TYPES.map((meta) => {
            const row = rows.find((r) => r.docType === meta.id);
            const draft = drafts[meta.id];
            if (!row || !draft) {
              return (
                <AdminListRow key={meta.id} className="px-4 py-3.5 text-sm">
                  <span className="text-[var(--color-muted)]">
                    Loading {meta.label}…
                  </span>
                </AdminListRow>
              );
            }
            return (
              <AdminListRow
                key={meta.id}
                className="flex flex-col items-stretch gap-3 px-4 py-3.5"
              >
                <div>
                  <div className="font-medium">{meta.label}</div>
                  <div className="mt-0.5 font-mono text-xs text-[var(--color-muted)]">
                    Next {row.suggested}
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  <label className="block text-xs text-[var(--color-muted)]">
                    Mode
                    <Select
                      className="mt-1"
                      value={draft.mode}
                      disabled={busy}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [meta.id]: {
                            ...draft,
                            mode: e.target.value as DocSeries["mode"],
                          },
                        }))
                      }
                    >
                      <option value="auto">Auto</option>
                      <option value="manual_prefilled">
                        Manual (prefilled)
                      </option>
                      <option value="manual">Manual (blank)</option>
                    </Select>
                  </label>
                  <label className="block text-xs text-[var(--color-muted)]">
                    Prefix
                    <Input
                      className="mt-1 font-mono"
                      value={draft.prefix}
                      disabled={busy}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [meta.id]: { ...draft, prefix: e.target.value },
                        }))
                      }
                    />
                  </label>
                  <label className="block text-xs text-[var(--color-muted)]">
                    Pad width
                    <Input
                      className="mt-1 font-mono"
                      inputMode="numeric"
                      value={draft.padWidth}
                      disabled={busy}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [meta.id]: { ...draft, padWidth: e.target.value },
                        }))
                      }
                    />
                  </label>
                </div>
                <div>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() => void onSave(meta.id)}
                  >
                    Save
                  </Button>
                </div>
              </AdminListRow>
            );
          })}
        </AdminList>
      </div>

      <p className="mt-8 flex flex-wrap gap-3 text-sm">
        <Link
          to={`/c/${companySlug}/settings/org`}
          className="no-underline"
        >
          ← Org settings
        </Link>
        <Link
          to={`/c/${companySlug}/settings/pay-methods`}
          className="no-underline"
        >
          Pay methods
        </Link>
      </p>
    </PageFrame>
  );
}
